import { z } from 'zod';
import { instantSchema, sha256Schema, uuidSchema } from './primitives.js';
const httpsOriginSchema = z
    .string()
    .url()
    .refine((value) => {
    const url = new URL(value);
    return url.protocol === 'https:' && url.origin === value && url.pathname === '/';
}, 'expected an HTTPS origin');
const httpsRedirectSchema = z
    .string()
    .url()
    .refine((value) => {
    const url = new URL(value);
    return (url.protocol === 'https:' &&
        url.username === '' &&
        url.password === '' &&
        url.search === '' &&
        url.hash === '');
}, 'redirect must be an exact HTTPS URL without credentials, query, or fragment');
export const bffSecurityContractSchema = z
    .strictObject({
    contract_version: z.literal('bff_security.v1'),
    application_origin: httpsOriginSchema,
    service_worker_scope: z.literal('/app/'),
    bff_prefix: z.literal('/bff/'),
    email_callback_path: z.literal('/app/auth/callback'),
    session_cookie: z.strictObject({
        name: z.literal('__Host-dmf_session'),
        http_only: z.literal(true),
        secure: z.literal(true),
        same_site: z.enum(['Strict', 'Lax']),
        path: z.literal('/'),
        domain: z.null(),
        max_age_seconds: z.number().int().safe().positive().max(86_400),
        rotation_interval_seconds: z.number().int().safe().positive().max(3_600),
    }),
    csrf: z.strictObject({
        exact_origin_required: z.literal(true),
        sec_fetch_site_same_origin_required: z.literal(true),
        per_session_token_required: z.literal(true),
    }),
    redirect_allowlist: z.array(httpsRedirectSchema).min(1).max(32),
    content_security_policy: z.string().min(1).max(4_096),
    browser_access_token_exposure: z.literal(false),
    browser_refresh_token_exposure: z.literal(false),
    browser_service_role_token_exposure: z.literal(false),
    server_token_storage: z.literal('encrypted_server_session_store'),
    service_worker_intercepts_bff: z.literal(false),
})
    .superRefine((value, context) => {
    if (value.session_cookie.rotation_interval_seconds >= value.session_cookie.max_age_seconds) {
        context.addIssue({
            code: 'custom',
            path: ['session_cookie', 'rotation_interval_seconds'],
            message: 'session must rotate before expiry',
        });
    }
    const appOrigin = new URL(value.application_origin).origin;
    value.redirect_allowlist.forEach((redirect, index) => {
        if (new URL(redirect).origin !== appOrigin) {
            context.addIssue({
                code: 'custom',
                path: ['redirect_allowlist', index],
                message: 'redirect allowlist must remain same-origin',
            });
        }
    });
    if (!value.redirect_allowlist.includes(`${appOrigin}${value.email_callback_path}`)) {
        context.addIssue({
            code: 'custom',
            path: ['redirect_allowlist'],
            message: 'redirect allowlist must include the exact installed-PWA email callback',
        });
    }
    const cspError = validateContentSecurityPolicy(value.content_security_policy);
    if (cspError !== null) {
        context.addIssue({
            code: 'custom',
            path: ['content_security_policy'],
            message: cspError,
        });
    }
});
function validateContentSecurityPolicy(policy) {
    if (/[\r\n\0,]/.test(policy)) {
        return 'content security policy must be a single canonical header value';
    }
    const optionalFetchDirectives = new Set([
        'script-src',
        'script-src-elem',
        'script-src-attr',
        'style-src',
        'style-src-elem',
        'style-src-attr',
        'connect-src',
        'img-src',
        'worker-src',
        'font-src',
        'frame-src',
        'child-src',
        'media-src',
        'manifest-src',
    ]);
    const required = new Map([
        ['default-src', ["'self'"]],
        ['object-src', ["'none'"]],
        ['base-uri', ["'none'"]],
        ['frame-ancestors', ["'none'"]],
        ['form-action', ["'self'"]],
    ]);
    const allowedDirectiveNames = new Set([
        ...required.keys(),
        ...optionalFetchDirectives,
        'upgrade-insecure-requests',
    ]);
    const directives = new Map();
    for (const rawDirective of policy.split(';')) {
        const trimmed = rawDirective.trim();
        if (trimmed === '') {
            continue;
        }
        const [name, ...sources] = trimmed.split(/\s+/);
        if (name === undefined || !/^[a-z][a-z0-9-]*$/.test(name)) {
            return 'content security policy contains an invalid directive name';
        }
        if (directives.has(name)) {
            return `content security policy cannot repeat ${name}`;
        }
        if (!allowedDirectiveNames.has(name)) {
            return `content security policy contains unsupported directive ${name}`;
        }
        if (name === 'upgrade-insecure-requests' && sources.length !== 0) {
            return 'upgrade-insecure-requests cannot carry a value';
        }
        if (optionalFetchDirectives.has(name) &&
            !(sources.length === 1 && (sources[0] === "'self'" || sources[0] === "'none'"))) {
            return `${name} must be exactly 'self' or 'none'`;
        }
        directives.set(name, sources);
    }
    for (const [name, expectedSources] of required) {
        const actualSources = directives.get(name);
        if (actualSources === undefined ||
            actualSources.length !== expectedSources.length ||
            actualSources.some((source, index) => source !== expectedSources[index])) {
            return `${name} must be exactly ${expectedSources.join(' ')}`;
        }
    }
    return null;
}
export const authFlowContractSchema = z.strictObject({
    contract_version: z.literal('auth_flow.v1'),
    provider: z.literal('supabase_auth'),
    provider_adapter_boundary: z.literal('server_only'),
    invitation_only: z.literal(true),
    public_signup_enabled: z.literal(false),
    passkey_status: z.literal('experimental_adapter_gated'),
    passkey_registration_requires_verified_email: z.literal(true),
    email_link: z.strictObject({
        callback_path: z.literal('/app/auth/callback'),
        one_time_exchange: z.literal(true),
        pre_ticket_before_session_creation: z.literal(true),
        link_scanner_safe_exchange: z.literal(true),
    }),
    browser_receives_provider_access_token: z.literal(false),
    browser_receives_provider_refresh_token: z.literal(false),
    offline_profile_continues_after_verified_pairing: z.literal(true),
    server_revocation_applies_on_next_connection: z.literal(true),
});
export const recoveryObjectivesSchema = z.strictObject({
    contract_version: z.literal('recovery_objectives.v1'),
    local_crash_rpo_seconds: z.literal(0),
    reference_sync_queue_target_seconds: z.literal(10),
    disaster_backup_rpo_seconds: z.literal(86_400),
    disaster_restore_rto_seconds: z.literal(28_800),
    json_self_restore_target_seconds: z.literal(1_800),
});
export const frozenRecoveryObjectives = recoveryObjectivesSchema.parse({
    contract_version: 'recovery_objectives.v1',
    local_crash_rpo_seconds: 0,
    reference_sync_queue_target_seconds: 10,
    disaster_backup_rpo_seconds: 86_400,
    disaster_restore_rto_seconds: 28_800,
    json_self_restore_target_seconds: 1_800,
});
export const immutableCheckpointSchema = z.strictObject({
    checkpoint_id: uuidSchema,
    checkpoint_kind: z.enum(['local_immutable_outside_active_db', 'server_restore_checkpoint']),
    dataset_epoch: uuidSchema,
    payload_sha256: sha256Schema,
    created_at: instantSchema,
    immutable: z.literal(true),
    validation_status: z.literal('passed'),
    retention_state: z.literal('retain_until_replacement_verified'),
});
//# sourceMappingURL=platform.js.map